import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Quotes } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { media } from "@/data/content";
import { Reveal } from "@/components/Reveal";

export const Testimonials = () => {
  const { t } = useLang();
  const items = t.testimonials.items;
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);

  const go = useCallback((d) => {
    setDir(d);
    setIdx((p) => (p + d + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    const id = setInterval(() => go(1), 6500);
    return () => clearInterval(id);
  }, [go]);

  const current = items[idx];

  return (
    <section data-testid="testimonials" className="relative bg-[#F3EFEA] py-24 md:py-36 overflow-hidden">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <Reveal className="text-center mb-14">
          <p className="text-sm tracking-luxe uppercase text-[#A39B8E] mb-6">{t.testimonials.kicker}</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            {t.testimonials.title}
          </h2>
        </Reveal>

        <div className="relative min-h-[340px] md:min-h-[280px] flex items-center justify-center">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={idx}
              custom={dir}
              initial={{ opacity: 0, x: dir * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -60 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="text-center max-w-3xl"
            >
              <Quotes size={44} weight="fill" className="mx-auto text-[#C9BEAF]" />
              <p className="mt-6 font-display font-light italic text-2xl md:text-3xl leading-snug text-[#1A1A1A]">
                “{current.quote}”
              </p>
              <div className="mt-8 flex flex-col items-center gap-3">
                <img
                  src={media.testimonials[idx % media.testimonials.length]}
                  alt={current.name}
                  className="h-14 w-14 rounded-full object-cover grayscale"
                />
                <div>
                  <p className="font-medium">{current.name}</p>
                  <p className="text-sm text-[#1A1A1A]/50">{current.role}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            data-testid="testimonial-prev"
            onClick={() => go(-1)}
            className="h-11 w-11 border border-black/15 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => { setDir(i > idx ? 1 : -1); setIdx(i); }}
                className={`h-1.5 transition-all duration-300 ${i === idx ? "w-8 bg-[#8B7355]" : "w-4 bg-black/20"}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          <button
            data-testid="testimonial-next"
            onClick={() => go(1)}
            className="h-11 w-11 border border-black/15 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors"
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};
